import { useEffect, useRef, useState } from "react";
import { Search, Plus, Check, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400";

// Meeting 3 (Package 42) — the vendor field is the QuickBooks vendor list,
// searched as you type. "Add new" creates the vendor in QuickBooks with
// address, phone, email and account number, then selects it.
export default function QboVendorPicker({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // full vendor record being edited
  const boxRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/contractor/qbo-vendors", { params: { q: query } }).then((r) => setResults(r.data.vendors)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (v) => { onChange({ qbo_id: v.qbo_id, name: v.name }); setQuery(""); setOpen(false); };

  if (value) {
    return (
      <div className="flex items-center gap-2 h-10 px-3 rounded-lg border border-sky-200 bg-sky-50 text-sm" data-testid="qbo-vendor-selected">
        <Check size={14} className="text-sky-600 shrink-0" />
        <span className="flex-1 truncate font-medium">{value.name}</span>
        <span className="text-[11px] text-slate-400 shrink-0">QuickBooks</span>
        <button type="button" onClick={async () => {
          const { data } = await api.get("/contractor/qbo-vendors", { params: { q: value.name } });
          setEditing(data.vendors.find((v) => v.qbo_id === value.qbo_id) || { qbo_id: value.qbo_id, name: value.name });
        }} className="text-slate-400 hover:text-sky-600" title="Edit this vendor in QuickBooks" data-testid="qbo-vendor-edit"><Pencil size={13} /></button>
        <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-slate-700" title="Change vendor"><X size={14} /></button>
        <VendorDialog open={!!editing} vendor={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={(v) => { setEditing(null); onChange({ qbo_id: v.qbo_id, name: v.name }); }} />
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={query} onChange={(e) => { setQuery(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder="Search your QuickBooks vendors…" data-testid="qbo-vendor-search" className={`${inputCls} pl-8`}
        />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="qbo-vendor-results">
          {results.map((v) => (
            <button type="button" key={v.qbo_id} onClick={() => pick(v)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-[13px]" data-testid={`qbo-vendor-option-${v.qbo_id}`}>
              <div className="font-medium">{v.name}</div>
              {(v.phone || v.email) && <div className="text-[11.5px] text-slate-400">{[v.phone, v.email].filter(Boolean).join(" · ")}</div>}
            </button>
          ))}
          {results.length === 0 && <div className="px-3 py-2 text-[12.5px] text-slate-400">No match in QuickBooks.</div>}
          <button type="button" onClick={() => { setAdding(true); setOpen(false); }} className="w-full text-left px-3 py-2.5 border-t border-slate-100 text-[13px] font-semibold text-sky-600 hover:bg-sky-50 flex items-center gap-1.5" data-testid="qbo-vendor-add-new">
            <Plus size={13} /> Add {query.trim() ? `"${query.trim()}"` : "a new vendor"} to QuickBooks
          </button>
        </div>
      )}
      <VendorDialog open={adding} initialName={query.trim()} onOpenChange={setAdding} onSaved={pick} />
    </div>
  );
}

// Add a vendor to QuickBooks, or (with `vendor`) edit one already there.
function VendorDialog({ open, initialName, vendor, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = !!vendor;
  useEffect(() => {
    if (!open) return;
    const a = vendor?.address || {};
    setForm({
      name: vendor?.name || initialName || "", address_line1: a.address_line1 || "", city: a.city || "", state: a.state || "", zip: a.zip || "",
      phone: vendor?.phone || "", email: vendor?.email || "", account_number: vendor?.account_number || "",
    });
  }, [open, initialName, vendor]);
  const onCreated = onSaved;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Vendor name is required");
    setSaving(true);
    try {
      const body = {
        name: form.name, phone: form.phone || null, email: form.email || null, account_number: form.account_number || null,
        address: { address_line1: form.address_line1 || null, city: form.city || null, state: form.state || null, zip: form.zip || null },
      };
      const { data } = isEdit ? await api.patch(`/contractor/qbo-vendors/${vendor.qbo_id}`, body) : await api.post("/contractor/qbo-vendors", body);
      toast.success(isEdit ? `${data.name} updated in QuickBooks` : `${data.name} added to QuickBooks`);
      onOpenChange(false);
      onCreated(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.vendor) {
        toast.info(detail.message);
        onOpenChange(false);
        onCreated(detail.vendor);
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't add the vendor");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="qbo-add-vendor-modal">
        <DialogHeader><DialogTitle>{isEdit ? "Edit vendor in QuickBooks" : "Add vendor to QuickBooks"}</DialogTitle></DialogHeader>
        <div className="space-y-2.5 mt-2">
          <input value={form.name || ""} onChange={set("name")} placeholder="Vendor name *" data-testid="qbo-new-vendor-name" className={inputCls} />
          <input value={form.address_line1 || ""} onChange={set("address_line1")} placeholder="Street address" className={inputCls} />
          <div className="grid grid-cols-[1fr_80px_100px] gap-2">
            <input value={form.city || ""} onChange={set("city")} placeholder="City" className={inputCls} />
            <input value={form.state || ""} onChange={set("state")} placeholder="State" maxLength={2} className={inputCls} />
            <input value={form.zip || ""} onChange={set("zip")} placeholder="ZIP" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.phone || ""} onChange={set("phone")} placeholder="Phone" className={inputCls} />
            <input value={form.email || ""} onChange={set("email")} type="email" placeholder="Email" className={inputCls} />
          </div>
          <input value={form.account_number || ""} onChange={set("account_number")} placeholder="Your account # with this vendor" className={inputCls} />
          <button disabled={saving} onClick={save} data-testid="qbo-save-new-vendor" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-60">
            {saving ? "Saving…" : isEdit ? "Save to QuickBooks" : "Add to QuickBooks"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
